/**
 * Dinheiro e quantidade — a convenção ÚNICA do PharuERP (V8/V22/V20, D-6A).
 *
 * Antes existiam 6 convenções de digitação, e duas divergiam 100× entre si:
 * a máscara de centavos (direita→esquerda, tipo caixa de banco) fazia
 * "80000" virar **R$ 800,00**, enquanto a digitação livre fazia virar
 * **R$ 80.000,00**. Foi esse par que apareceu no teste ao vivo do ERP.
 *
 * Decisão do dono (2026-08-11): vale a **digitação livre pt-BR** — vírgula é
 * decimal, ponto é milhar, e quem digita "80000" quer oitenta mil. É a
 * convenção que já valia nos dois campos onde entra o valor grande do
 * negócio (preço ao cliente do motor e valor do móvel), e não obriga a
 * digitar 7 caracteres para lançar R$ 80.000.
 *
 * Use SEMPRE estes helpers em campo de dinheiro novo. Não escreva outro
 * parser: foi assim que chegamos a seis.
 */

/**
 * Texto digitado -> Number | null.
 *
 * Aceita as formas que uma pessoa realmente digita:
 *   "1.500,50" -> 1500.5   (ponto = milhar, vírgula = decimal)
 *   "1500,5"   -> 1500.5
 *   "1500.5"   -> 1500.5   (sem vírgula, ponto único é decimal)
 *   "80000"    -> 80000
 *   "R$ 1.200" -> 1200     (símbolo e espaços são ignorados)
 *   ""         -> null     (vazio é ausência, não zero)
 */
export function parseDinheiro(texto) {
  if (texto === null || texto === undefined) return null;
  let t = String(texto).trim();
  if (!t) return null;
  // Tira tudo que não é dígito, vírgula, ponto ou sinal — "R$", espaço,
  // espaço não-quebrável (que vem do toLocaleString) saem aqui.
  t = t.replace(/[^\d,.-]/g, "");
  if (!t) return null;
  if (t.includes(",")) {
    // Com vírgula, o ponto só pode ser milhar.
    t = t.replace(/\./g, "").replace(",", ".");
  } else if (PONTO_DE_MILHAR.test(t)) {
    // Sem vírgula, o ponto é ambíguo: em "1500.5" ele é decimal, em
    // "80.000" é milhar. Quem desempata é o TAMANHO do grupo — separador
    // de milhar sempre vem seguido de exatamente 3 dígitos.
    //
    // Sem este ramo, "80.000" virava 80 e "R$ 1.200" virava 1,20 — mil
    // vezes menos, sem erro na tela. Pior: "1.234.567" virava null, porque
    // Number() não engole dois pontos, e o campo simplesmente não salvava.
    t = t.replace(/\./g, "");
  }
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

/**
 * Grupos de milhar pt-BR: 1 a 3 dígitos, depois grupos de exatamente 3.
 * "80.000", "1.200", "1.234.567" casam; "1500.5", "12.34", "1.23456" não.
 *
 * "1.234" é lido como mil duzentos e trinta e quatro, não como um inteiro
 * e 234 milésimos: dinheiro tem dois decimais, então três dígitos depois
 * do ponto nunca são centavos.
 */
export const PONTO_DE_MILHAR = /^-?\d{1,3}(\.\d{3})+$/;

/** Number -> "1.234,56" (sem símbolo — para dentro de input). */
export function formatarDinheiro(valor) {
  const n = Number(valor);
  if (!Number.isFinite(n)) return "0,00";
  return n.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/** Number -> "R$ 1.234,56" (com símbolo — para exibição). */
export function formatarReaisBR(valor) {
  const n = Number(valor);
  if (!Number.isFinite(n)) return "R$ 0,00";
  return n.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

/**
 * Quantidade — INTEIRA sem separador de milhar (V22).
 *
 * `toLocaleString` põe ponto de milhar em quantidade, e "1.000 unidades"
 * lido rápido vira "mil vírgula zero" ou "uma unidade" dependendo de quem
 * lê. Quantidade não é dinheiro: 1000 peças é "1000".
 *
 * Quando a quantidade tem fração real (m², metros de fita), use
 * `formatarQuantidadeFracionaria`.
 */
export function formatarQuantidade(valor) {
  const n = Number(valor);
  if (!Number.isFinite(n)) return "0";
  if (Number.isInteger(n)) return String(n);
  return formatarQuantidadeFracionaria(n);
}

/**
 * Percentual JÁ na escala 0–100 -> "66,7%" (pt-BR, uma casa decimal).
 *
 * FORM-24: o Dashboard de formulários mostrava o float cru ("66.7%") e a
 * tela de Respostas arredondava por conta própria ("67%") — o mesmo número
 * com dois textos, e nenhum no formato pt-BR. Percentual novo usa este.
 *
 * Sem número é ausência de dado ("—"), não 0%: conversão sem denominador
 * não é conversão zero.
 */
export function formatarPercentual(valor) {
  if (valor === null || valor === undefined || valor === "") return "—";
  const n = Number(valor);
  if (!Number.isFinite(n)) return "—";
  const numero = n.toLocaleString("pt-BR", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
  return `${numero}%`;
}

/** Quantidade com casas decimais ("2,5" m²) — sem milhar, vírgula decimal. */
export function formatarQuantidadeFracionaria(valor, casas = 3) {
  const n = Number(valor);
  if (!Number.isFinite(n)) return "0";
  // Tira zeros à direita: 2,500 -> 2,5 ; 2,000 -> 2
  const fixo = n.toFixed(casas).replace(/\.?0+$/, "");
  return fixo.replace(".", ",");
}

/**
 * Plural em português — "1 móvel" / "2 móveis" (V20).
 *
 * A auditoria pegou "0 movels". O caso literal já foi corrigido, mas a
 * classe continua: `${n} itens` espalhado pelo código. Este helper existe
 * para que campo novo não nasça assim.
 *
 *   plural(1, "móvel", "móveis")  -> "1 móvel"
 *   plural(0, "móvel", "móveis")  -> "0 móveis"   (zero é plural em pt-BR)
 *   plural(2, "item", "itens")    -> "2 itens"
 */
export function plural(quantidade, singular, plural_) {
  const n = Number(quantidade) || 0;
  const palavra = Math.abs(n) === 1 ? singular : plural_;
  return `${formatarQuantidade(n)} ${palavra}`;
}

/**
 * Data "YYYY-MM-DD" (date-only do backend) -> "DD/MM/AAAA".
 *
 * ARMADILHA que este helper existe para evitar (V16): `new Date("2026-08-11")`
 * é parseado como **meia-noite UTC**. No fuso de São Paulo (UTC−3) isso vira
 * 21h do dia 10 — e a tela mostra o dia ANTERIOR. Vencimento, competência,
 * data agendada: todos são date-only e caíam nisso.
 *
 * Datetime COM fuso (`2026-08-11T15:43:00Z`) não tem esse problema: aí
 * `new Date(...).toLocaleString("pt-BR")` converte corretamente.
 */
export function formatarDataLocal(iso) {
  if (!iso) return "—";
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso));
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  // Datetime com fuso: deixa o browser converter.
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("pt-BR");
}

/**
 * ISO -> `Date` no fuso LOCAL, sem o off-by-one de data-só.
 *
 * `new Date("2026-08-21")` é meia-noite **UTC** — em São Paulo, 21h do
 * dia 20. Toda tela que formata `data_proxima_acao`, validade ou prazo
 * (todos `DateField` no backend, portanto date-only) mostra o dia
 * anterior. Foi o que o reteste de 2026-08-18 pegou no follow-up:
 * escolheu 21/08, a tela exibiu 20/08 — e a data estava gravada certa.
 *
 * Use quando a tela precisa de opções próprias de formatação; quando
 * DD/MM/AAAA serve, `formatarDataLocal` já resolve.
 */
export function dataLocalDe(iso) {
  if (!iso) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso));
  // Meia-noite LOCAL, não UTC.
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}
